import { decimalIntegerDigits } from '@snap-types/schemas/decimal';
import { Decimal } from '@snap-types/types/decimal';

type ParseNumberInputParams = {
  value: string | Decimal;
  requiredMessage: string;
  invalidMessage: string;
};

type ParseNumberInputResult =
  | { parsed: number; error?: never }
  | { parsed?: never; error: string };

export function parseNumberInput({
  value,
  requiredMessage,
  invalidMessage,
}: ParseNumberInputParams): ParseNumberInputResult {
  const input = value.toString().trim();

  if (input === '') {
    return { error: requiredMessage };
  }

  const parsed = Number(input);

  if (Number.isNaN(parsed)) {
    return { error: invalidMessage };
  }

  return { parsed };
}

type ValidateDecimalPrecisionParams = {
  value: string | Decimal;
  maxDecimalPlaces: number;
  maxIntegerDigits?: number;
  decimalPlacesMessage: string;
  integerDigitsMessage?: string;
};

export function validateDecimalPrecision({
  value,
  maxDecimalPlaces,
  maxIntegerDigits,
  decimalPlacesMessage,
  integerDigitsMessage,
}: ValidateDecimalPrecisionParams): string | undefined {
  try {
    const input = value.toString().trim();

    if (input === '') {
      return undefined;
    }

    const decimal = new Decimal(input);

    if (decimal.isNaN()) {
      return undefined;
    }

    if (decimal.dp() > maxDecimalPlaces) {
      return decimalPlacesMessage;
    }

    if (maxIntegerDigits !== undefined) {
      const integerDigits = decimalIntegerDigits(decimal);

      if (integerDigits > maxIntegerDigits) {
        return (
          integerDigitsMessage ??
          `Value cannot have more than ${maxIntegerDigits} digits before the decimal point`
        );
      }
    }

    return undefined;
  } catch {
    return undefined;
  }
}
